/**
 * YouTube IFrame Player API loader + helpers.
 *
 * Loads https://www.youtube.com/iframe_api once (shared across every video on
 * the page) and resolves with the global `window.YT` object once it's ready.
 * Used together with the lite-embed pattern in LessonPlayer: we don't call
 * this until the user actually clicks play, so pages with YouTube lessons
 * don't pay the iframe/JS cost until needed.
 */
let ytApiPromise = null;

export function loadYouTubeIframeApi() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousCallback === 'function') previousCallback();
      resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return ytApiPromise;
}

/** Extract an 11-char YouTube video ID from watch/embed/short/youtu.be URLs. */
export function extractYouTubeId(url) {
  if (!url) return null;
  const match = String(url).match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

export function youtubeThumbnailUrl(videoId) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

// player.getPlaybackQuality()/setPlaybackQuality() levels <-> our UI labels.
export const YT_QUALITY_LEVELS = [
  { value: 'auto', ytValue: 'default', label: 'Auto' },
  { value: 'hd1080', ytValue: 'hd1080', label: '1080p' },
  { value: 'hd720', ytValue: 'hd720', label: '720p' },
  { value: 'large', ytValue: 'large', label: '480p' },
  { value: 'medium', ytValue: 'medium', label: '360p' },
  { value: 'small', ytValue: 'small', label: '240p' },
  { value: 'tiny', ytValue: 'tiny', label: '144p' },
];

export function ytQualityLabel(ytValue) {
  const found = YT_QUALITY_LEVELS.find((q) => q.ytValue === ytValue);
  return found ? found.label : 'Auto';
}
